import { GlobalVariables } from 'app/services/globals';
import { Metamodel } from 'app/model/base/model';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

/**
 * The multi-cloud "Databricks" connector is meant to behave exactly like the existing
 * "Azure Databricks Delta Lake" connector (catalog/database/table navigation, delta-only
 * validation, staging rules, sink file naming, etc.).
 *
 * These helpers centralize the "is this dataset part of the Databricks Delta Lake family?"
 * decision so the many imperative checks scattered across the copy wizard, copy activity
 * validation and data-store navigation don't have to duplicate (and drift out of sync with)
 * the pairing of `AzureDatabricksDeltaLakeDataset` and `DatabricksDataset`.
 *
 * The multi-cloud Databricks branch is gated by the same feature flag used everywhere else
 * (`GlobalVariables.enableDatabricks`, which is only ever true in Fabric DPE).
 */
export function isDatabricksFamilyDatasetType(datasetType: string | null | undefined): boolean {
    if (datasetType == null) {
        return false;
    }
    if (datasetType === adfModelTypeMap.azureDatabricksDeltaLake.datasetType) {
        return true;
    }
    if (datasetType === adfModelTypeMap.databricks.datasetType) {
        return GlobalVariables.enableDatabricks && __IS_FABRIC_DPE__;
    }
    return false;
}

/**
 * Dataset-instance overload of {@link isDatabricksFamilyDatasetType}. Uses the dataset's
 * model name (identical to how the copy-activity validation dispatch resolves the dataset
 * type) so it stays consistent with `instanceof` checks without importing the dataset models.
 */
export function isDatabricksFamilyDataset(dataset: unknown): boolean {
    if (dataset == null) {
        return false;
    }
    return isDatabricksFamilyDatasetType(Metamodel.modelOf(dataset)?.name);
}

/**
 * Whether the up-front `getItemMetadata` (`fields: ['format']`) probe that rejects non-Delta
 * tables should run. Fabric skips it: the extra per-table round trip is not run there, so the
 * format is left to the backend at execution time. ADF/Synapse (including mounted data factory)
 * keep the original client-side check.
 */
export function shouldValidateDeltaTableFormat(datasetType: string | null | undefined): boolean {
    return !__IS_FABRIC_DPE__ && isDatabricksFamilyDatasetType(datasetType);
}

/**
 * Connector display name for validation messages, so the multi-cloud connector is not
 * labelled "Azure Databricks Delta Lake".
 */
export function getDatabricksFamilyDisplayName(dataset: unknown): string {
    return dataset != null && Metamodel.modelOf(dataset)?.name === adfModelTypeMap.databricks.datasetType
        ? stringResources.Activity_Category_Databricks
        : stringResources.AzureDatabricksDeltaLake_DisplayText;
}

/**
 * Linked-service-type counterpart of {@link isDatabricksFamilyDatasetType}. Used where the
 * connector is identified by its linked-service type string (e.g. copy-job staging
 * validation) rather than by the dataset model.
 */
export function isDatabricksFamilyLinkedServiceType(linkedServiceType: string | null | undefined): boolean {
    if (linkedServiceType == null) {
        return false;
    }
    if (linkedServiceType === adfModelTypeMap.azureDatabricksDeltaLake.linkedServiceType) {
        return true;
    }
    if (linkedServiceType === adfModelTypeMap.databricks.linkedServiceType) {
        return GlobalVariables.enableDatabricks && __IS_FABRIC_DPE__;
    }
    return false;
}
